import axiosConfig from "./axiosConfig";
import { HealthEvent } from "./getSiteHealthEvents";

export interface HealthOverviewSite {
  siteId: string;
  siteName: string;
  openEvents: HealthEvent[];
  availability: { pct: number | null };
}

export interface SiteAvailability {
  siteId: string;
  siteName: string;
  pct: number | null;
  ryg: string;
  // Per-period breakdowns (added alongside the legacy flat pct/ryg).
  lastMonth?: { pct: number | null; ryg: string };
  last12Months?: { pct: number | null; ryg: string };
  // Effective contracted threshold (falls back to fleet 99% when the site
  // has no configured `contracted_availability`); flag distinguishes the
  // configured vs default case so the breakdown can mark defaults with `*`.
  contractedPct?: number | null;
  contractedPctIsDefault?: boolean;
  monthsAnalysed?: number;
  latestMonthLabel?: string | null;
}

export interface PRPeriodValue {
  pct: number | null;
  ryg: string | null;
  // Per-period contracted GPR threshold (only populated for the GPR
  // periods, not OPR — OPR uses fixed global thresholds). The default
  // flag is true when the site has no configured gpr_rule or
  // contracted_gpr; the value then comes from the fleet fallback.
  gprPct?: number | null;
  gprPctIsDefault?: boolean;
  designedPrPct?: number | null;
}

export interface SitePR {
  siteId: string;
  siteName: string;
  lastMonth: PRPeriodValue | null;
  lastMonthLabel?: string;
  last12Months: PRPeriodValue | null;
  oprLastMonth?: PRPeriodValue | null;
  oprLast12Months?: PRPeriodValue | null;
  monthsAnalysed?: number;
  latestMonthLabel?: string | null;
  curtailmentLossMwh: number | null;
}

export interface RecentHealthEvent extends HealthEvent {
  siteName: string;
}

export interface HealthOverviewResponse {
  sites: HealthOverviewSite[];
  badgeCounts: Record<string, number>;
  aggregateAvailability: { pct: number | null; ryg: string };
  allSiteAvailability: SiteAvailability[];
  aggregatePR?: {
    lastMonth: PRPeriodValue;
    last12Months: PRPeriodValue;
  };
  aggregateOPR?: {
    lastMonth: PRPeriodValue;
    last12Months: PRPeriodValue;
  };
  allSitePR?: SitePR[];
}

export interface HealthOverviewInitResponse {
  operationKey: string;
  status: string;
}

const postHealthOverview = async (
  authToken: string,
): Promise<HealthOverviewInitResponse> => {
  const { data } = await axiosConfig.post(
    "/health/events",
    {},
    {
      headers: { Authorization: `Bearer ${authToken}` },
    },
  );
  return data;
};

export default postHealthOverview;
